import { z } from "zod";
import {
  ANONYMIZED_MEMBER_DISPLAY_NAME,
  EVIDENCE_UNVERIFIED_LABEL,
  VERIFICATION_STATUS,
} from "@goppy/domain";

/* ===========================================================================
 * US-E19.F5-0204 (@UC9312 @UC9313 @UC9314) — GDPR ẩn danh nhất quán (người-rời) +
 * BẰNG CHỨNG = CLAIM (không phải fact). 🔴 @gate pii + money.
 *
 * (a) 🔴 EVIDENCE-LÀ-CLAIM: body đính kèm bằng chứng **KHÔNG CÓ** field `verificationStatus` và
 *     schema `.strict()` ⇒ client gửi `verificationStatus: 'valid'`/`'verified'` ⇒ **400 Ở BIÊN**,
 *     0 row chạm DB. Tự-phong-xác-minh là trạng thái KHÔNG BIỂU DIỄN ĐƯỢC ở contract.
 * (b) Từ vựng DÙNG CHUNG với E19.F4 (`VERIFICATION_STATUS` domain = whitelist DB) — KHÔNG enum
 *     'claim'/'verified' thứ hai. "claim" = `unverified`; "verified" = `valid`.
 * (c) 🔴 KHÔNG PII trong response: bằng chứng trả `storageKey` (KEY R2, không phải URL/PII) +
 *     trạng thái. KHÔNG trả tên/email người nộp. Anonymize trả CON SỐ đã xoá, KHÔNG trả PII cũ.
 * (d) 🔴 KHÔNG TIỀN trong luồng này (G2): đính kèm/soát bằng chứng KHÔNG sinh bút toán ⇒ contract
 *     KHÔNG có field tiền / `ledgerTxId` nào (không biểu diễn được ⇒ không thể lặng lẽ đổi tiền).
 * ========================================================================= */

/* ─────────────────────── (1) GDPR — ẩn danh người-rời ─────────────────────── */

/** 🔴 Mã lỗi 409 khi còn ràng buộc tiền — REUSE `FINANCIAL_POSITION_CODE.NONZERO` (debts.ts). */
export const ANONYMIZE_BLOCKED_MESSAGE =
  "Không thể ẩn danh: thành viên còn ràng buộc tài chính chưa tất toán trong nhóm." as const;

/** Nhãn hiển thị người đã rời + đã ẩn danh (1 nguồn sự thật ở domain). */
export const ANONYMIZED_MEMBER_LABEL = ANONYMIZED_MEMBER_DISPLAY_NAME;

/**
 * Kết quả ẩn danh người-rời. 🔴 KHÔNG chứa PII cũ (không email/tên/SĐT) — chỉ id + SỐ LƯỢNG row PII
 * đã dọn. `ledgerPreserved` = literal `true`: **không có cách nào** trả về "ledger đã bị xoá" ⇒ hành
 * vi hard-delete sổ cái KHÔNG BIỂU DIỄN ĐƯỢC (fail-loud, mirror `autoDeducted: z.literal(false)` F4).
 */
export const anonymizeLeftMemberResponseSchema = z
  .object({
    userId: z.string().uuid(),
    groupId: z.string().uuid(),
    status: z.literal("anonymized"),
    anonymizedAt: z.string().datetime(),
    /** 🔴 Bút toán GIỮ NGUYÊN (GDPR = ẩn danh, KHÔNG xoá sổ) — literal true. */
    ledgerPreserved: z.literal(true),
    /** Số row PII đã dọn (đếm — KHÔNG nội dung). */
    purged: z
      .object({
        notifications: z.number().int().nonnegative(),
        deviceTokens: z.number().int().nonnegative(),
        notificationPreferences: z.number().int().nonnegative(),
        socialAccounts: z.number().int().nonnegative(),
        identityIndexes: z.number().int().nonnegative(),
        payeeKyc: z.number().int().nonnegative(),
        /**
         * 🔴 US-AI-0442-S01 (@UC9699) — trạng thái "đã bỏ qua lời nhắc" (`ai_user_nudge_state`).
         *
         * KHÔNG phải PII nội dung, nhưng nó GẮN `userId` ⇒ là dữ liệu VỀ NGƯỜI DÙNG, và để lại thì
         * sau khi ẩn danh vẫn còn một khẳng định về một người đã bị ẩn danh. Bảng CACHE ⇒ xoá hàng
         * được. `.default(0)` để client cũ không vỡ.
         */
        nudgeStates: z.number().int().nonnegative().default(0),
        revokedSessions: z.number().int().nonnegative(),
      })
      .strict(),
    /** Nhãn thay thế hiển thị ở lịch sử họ có phần. */
    displayName: z.literal(ANONYMIZED_MEMBER_DISPLAY_NAME),
  })
  .strict();
export type AnonymizeLeftMemberResponse = z.infer<typeof anonymizeLeftMemberResponseSchema>;

/* ─────────────────────── (2) Evidence = CLAIM ─────────────────────── */

export const evidenceVerificationStatusSchema = z.enum([
  VERIFICATION_STATUS.VALID,
  VERIFICATION_STATUS.INVALID,
  VERIFICATION_STATUS.UNVERIFIED,
]);
export type EvidenceVerificationStatusDto = z.infer<typeof evidenceVerificationStatusSchema>;

/**
 * 🔴 Đính kèm bằng chứng vào case tranh chấp. **KHÔNG CÓ** field `verificationStatus` + `.strict()`
 * ⇒ client KHÔNG THỂ tự phong `valid`/`verified` (gửi lên ⇒ 400 ở biên, 0 row chạm DB).
 * `storageKey` = KEY R2 BỀN — chặn presigned URL ngay ở biên (mirror `attachComplianceDocSchema`).
 */
export const attachDisputeEvidenceSchema = z
  .object({
    storageKey: z
      .string()
      .trim()
      .min(1)
      .max(512)
      .refine((v) => !/^https?:\/\//i.test(v), {
        message: "storageKey phải là KEY lưu trữ, không phải URL (presigned URL không được lưu DB).",
      }),
    note: z.string().trim().max(500).optional(),
  })
  .strict();
export type AttachDisputeEvidenceBody = z.infer<typeof attachDisputeEvidenceSchema>;

/**
 * Response bằng chứng. `verificationStatus` + `label` ĐI ĐÔI: `unverified` ⇒ FE hiển thị
 * "Chưa xác minh" (không được render như sự thật đã kiểm chứng).
 * 🔴 KHÔNG có field tiền nào ⇒ đính kèm bằng chứng KHÔNG THỂ biểu diễn việc đổi tiền.
 */
export const disputeEvidenceResponseSchema = z
  .object({
    id: z.string().uuid(),
    disputeId: z.string().uuid(),
    verificationStatus: evidenceVerificationStatusSchema,
    /** Nhãn hiển thị cho trạng thái chưa xác minh (claim). */
    label: z.string(),
    storageKey: z.string(),
    verifiedAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
  })
  .strict();
export type DisputeEvidenceResponse = z.infer<typeof disputeEvidenceResponseSchema>;

/** Nhãn FE cho từng trạng thái xác minh (1 nguồn sự thật — không hardcode chuỗi rải rác). */
export const EVIDENCE_STATUS_LABEL: Readonly<Record<EvidenceVerificationStatusDto, string>> = {
  [VERIFICATION_STATUS.UNVERIFIED]: EVIDENCE_UNVERIFIED_LABEL,
  [VERIFICATION_STATUS.VALID]: "Đã xác minh",
  [VERIFICATION_STATUS.INVALID]: "Không hợp lệ",
} as const;

/**
 * Soát bằng chứng — CHỈ `valid|invalid` (KHÔNG cho quay về `unverified`: xoá dấu vết soát).
 * Hành động CÓ CHỦ THỂ: service ghi `verifiedBy` + `verifiedAt`; RBAC owner/admin/treasurer nhóm.
 */
export const verifyDisputeEvidenceSchema = z
  .object({
    verificationStatus: z.enum([VERIFICATION_STATUS.VALID, VERIFICATION_STATUS.INVALID]),
    reason: z.string().trim().max(500).optional(),
  })
  .strict();
export type VerifyDisputeEvidenceBody = z.infer<typeof verifyDisputeEvidenceSchema>;

/** 🔴 403 khi member thường (không thẩm quyền soát) cố chuyển trạng thái bằng chứng. */
export const EVIDENCE_VERIFY_FORBIDDEN_MESSAGE =
  "Chỉ chủ nhóm/quản trị/thủ quỹ mới được xác minh bằng chứng." as const;

/** 🔴 422 khi ai đó xin đổi tiền dựa trên bằng chứng CHƯA xác minh (claim-mù). */
export const EVIDENCE_NOT_VERIFIED_CODE = "EVIDENCE_NOT_VERIFIED" as const;
export const EVIDENCE_NOT_VERIFIED_MESSAGE =
  "Bằng chứng chưa được xác minh. Không thể thay đổi số tiền chỉ dựa trên bằng chứng chưa xác minh." as const;

/* ───────────── 🔴 US-E24.F16-0365 (@UC9606) · ĐƯA FILE LÊN để CÓ `storageKey` ───────────── */

/**
 * 🔴 Mắt xích còn thiếu (đo 06/08/2026): `attachDisputeEvidenceSchema` đòi `storageKey` là **KEY R2**
 * và chặn URL ngay ở biên — nhưng repo **không có đường nào** cho end-user đưa file lên R2 để có KEY
 * đó ⇒ mở được khiếu nại mà không nộp được bằng chứng.
 *
 * Đường mới là **multipart** (mirror `member-due-confirmations`), KHÔNG presigned-PUT: repo chưa từng
 * có lane presigned-PUT cho client, thêm nó là mở mô hình đe doạ mới (client ghi thẳng bucket ⇒ server
 * mất điểm kiểm magic-byte).
 */

/** Tên field multipart — hằng dùng chung API↔FE (khớp `me/avatar`, `funds/:id/qr`). */
export const DISPUTE_EVIDENCE_FILE_FIELD = "file" as const;

/**
 * Prefix vùng chứa object trên R2 cho bằng chứng khiếu nại. Tách khỏi `proofs/` (đường member-due)
 * để lifecycle/retention về sau đặt được riêng, nhưng **DÙNG CHUNG một cơ chế upload + một sniffer**.
 */
export const DISPUTE_EVIDENCE_KEY_PREFIX = "dispute-evidence" as const;

/** 400 khi multipart thiếu file. */
export const DISPUTE_EVIDENCE_MISSING_FILE_MESSAGE =
  "Thiếu tệp bằng chứng (field 'file')." as const;

/**
 * 🔴 US-E24.F16-0365-S02 (@UC9607) — KEY PHẢI THUỘC VỀ CHÍNH CASE ĐANG ĐÍNH KÈM.
 *
 * Lỗ đã có (phát hiện ở review PR #363): `attach` nhận `storageKey` là **chuỗi bất kỳ ≤512 ký tự**,
 * chỉ chặn `http(s)://`. Mà `GET /v1/disputes/:id/evidence` trả `storageKey` **thô** cho mọi member
 * ACTIVE của nhóm ⇒ B đọc được key ảnh sao kê ở case của A (cùng nhóm), rồi `attach` chính key đó
 * vào case của B ⇒ **nộp bằng chứng đi mượn**. Người soát (O/A/T) nhìn thấy một ảnh chuyển khoản
 * "hợp lệ" và có thể đánh `valid` ⇒ `assertEvidenceSupportsMoneyChange` mở cổng đổi tiền.
 *
 * Không có nhu cầu hợp lệ nào cho việc đính key của case này vào case khác ⇒ **fail-closed**: key
 * phải đúng dạng SERVER sinh ra ở `buildProofKey` cho ĐÚNG `disputeId` đó:
 *   `dispute-evidence/<disputeId>/<uuid>.<jpg|png|webp>`
 *
 * Kiểm ở SERVICE (không phải contract) vì hình dạng hợp lệ **phụ thuộc `disputeId` trên URL** —
 * thứ mà schema của body không nhìn thấy. Đặt hàm ở đây để API và test dùng CHUNG một định nghĩa,
 * không đẻ bản chép tay thứ hai (§3.8).
 */
const DISPUTE_EVIDENCE_KEY_EXTENSIONS = ["jpg", "png", "webp"] as const;

/** Dựng key đúng dạng cho một case — dùng trong test/fixture để khỏi chép tay chuỗi. */
export function buildDisputeEvidenceKey(
  disputeId: string,
  objectUuid: string,
  ext: (typeof DISPUTE_EVIDENCE_KEY_EXTENSIONS)[number] = "jpg",
): string {
  return `${DISPUTE_EVIDENCE_KEY_PREFIX}/${disputeId}/${objectUuid}.${ext}`;
}

/**
 * `true` ⇔ `key` là key do server sinh cho ĐÚNG `disputeId` này.
 * UUID nhận nibble version `[1-8]` (bài học `uuidv7-guard-regex-404-trap`: `[1-5]` làm guard mù với
 * uuid(7) — ở đây `randomUUID()` là v4, nhưng đóng hẹp theo v4 sẽ vỡ lặng nếu bộ sinh đổi sang v7).
 */
export function isDisputeEvidenceKeyFor(disputeId: string, key: string): boolean {
  const uuid = "[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}";
  const escaped = disputeId.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const re = new RegExp(
    `^${DISPUTE_EVIDENCE_KEY_PREFIX}/${escaped}/${uuid}\\.(${DISPUTE_EVIDENCE_KEY_EXTENSIONS.join("|")})$`,
    "i",
  );
  return re.test(key);
}

/** 422 khi key không thuộc về case đang đính kèm (hoặc không đúng dạng server sinh). */
export const DISPUTE_EVIDENCE_KEY_FOREIGN_CODE = "dispute_evidence_key_foreign" as const;
export const DISPUTE_EVIDENCE_KEY_FOREIGN_MESSAGE =
  "storageKey không thuộc về khiếu nại này. Hãy tải tệp lên qua chính khiếu nại đang thao tác." as const;

/** 400 khi lỗi HẠ TẦNG lưu trữ (KHÔNG che 413/415 — client cần biết nên nén hay đổi định dạng). */
export const DISPUTE_EVIDENCE_UPLOAD_FAILED_MESSAGE =
  "Tải bằng chứng lên thất bại. Vui lòng thử lại." as const;

/**
 * 🔴 Response bước upload — CHỈ `storageKey`, `.strict()`, và **cùng regex chặn URL** như attach.
 * Vì sao trả KEY chứ không trả URL: nếu upload trả URL thì client sẽ attach chính URL đó ⇒ presigned
 * URL vào DB qua cửa sau, hết hạn ⇒ bằng chứng biến mất. Schema này đóng cửa sau đó ngay ở contract.
 */
export const uploadDisputeEvidenceResponseSchema = z
  .object({
    storageKey: z
      .string()
      .trim()
      .min(1)
      .max(512)
      .refine((v) => !/^https?:\/\//i.test(v), {
        message: "storageKey phải là KEY lưu trữ, không phải URL (presigned URL không được lưu DB).",
      }),
  })
  .strict();
export type UploadDisputeEvidenceResponse = z.infer<typeof uploadDisputeEvidenceResponseSchema>;
